import { enqueueSnackbar } from 'notistack';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { SavedFilter } from '@/entities/saved-filter';

import { savedFiltersOptions } from '../savedFilters.options';
import { invalidateAndBroadcast, saveSavedFilterToApi } from './savedFiltersMutation.api';

export const useSaveFilterMutation = () => {
  const queryClient = useQueryClient();
  const queryKey = savedFiltersOptions.all();

  return useMutation({
    mutationKey: savedFiltersOptions.mutations.save(),

    mutationFn: async (savedFilter: SavedFilter): Promise<SavedFilter> => {
      const { id, ...payload } = savedFilter;
      await saveSavedFilterToApi(id, payload);
      return savedFilter;
    },

    onMutate: async (savedFilter: SavedFilter) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<SavedFilter[]>(queryKey);
      queryClient.setQueryData(queryKey, [...(previous ?? []), savedFilter]);
      return { previous };
    },

    onSuccess: async savedFilter => {
      enqueueSnackbar(`"${savedFilter.name}" saved`, { variant: 'success' });
      await invalidateAndBroadcast(queryClient);
    },

    onError: (_error, _savedFilter, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      enqueueSnackbar('Something went wrong saving your filter', { variant: 'error' });
    }
  });
};
